/**
 * WhatsApp Float Button
 * Handles analytics tracking and enhanced functionality
 */

class WhatsAppFloat {
  constructor() {
    this.button = document.querySelector('.whatsapp-float-button');
    this.init();
  }

  init() {
    if (!this.button) return;
    
    this.setupEventListeners();
    this.handleVisibility();
    this.setupAccessibility();
  }

  setupEventListeners() {
    if (!this.button) return;
    
    // Track clicks for analytics
    this.button.addEventListener('click', (e) => {
      this.trackClick();
      
      // Small delay to ensure tracking is sent before navigation
      e.preventDefault();
      setTimeout(() => {
        if (this.button && this.button.href) {
          window.open(this.button.href, '_blank', 'noopener,noreferrer');
        }
      }, 100);
    });

    // Add hover effects for better UX
    this.button.addEventListener('mouseenter', () => {
      this.onHover();
    });

    this.button.addEventListener('mouseleave', () => {
      this.onHoverEnd();
    });
  }

  trackClick() {
    try {
      // Google Analytics 4 tracking
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'click', {
          event_category: 'WhatsApp',
          event_label: 'Float Button',
          value: 1
        });
      }

      // Facebook Pixel tracking
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'Contact', {
          content_name: 'WhatsApp Float Button'
        });
      }

      // Shopify Analytics
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib) {
        window.ShopifyAnalytics.lib.track('WhatsApp Click', {
          source: 'float_button'
        });
      }
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }

    // Custom tracking event
    this.dispatchCustomEvent('whatsapp:click');
  }

  onHover() {
    // Add subtle animation or effect on hover
    if (this.button && this.button.style) {
      this.button.style.animationPlayState = 'paused';
    }
  }

  onHoverEnd() {
    // Resume animation
    if (this.button && this.button.style) {
      this.button.style.animationPlayState = 'running';
    }
  }

  handleVisibility() {
    // Hide/show button based on scroll position or page context
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateVisibility = () => {
      const currentScrollY = window.scrollY;
      
      // Hide button when scrolling up quickly (optional behavior)
      // Uncomment if you want this behavior:
      // if (currentScrollY < lastScrollY && currentScrollY > 200) {
      //   this.hideButton();
      // } else {
      //   this.showButton();
      // }
      
      lastScrollY = currentScrollY;
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateVisibility);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
  }

  hideButton() {
    if (!this.button || !this.button.style) return;
    this.button.style.transform = 'translateY(100px) scale(0.8)';
    this.button.style.opacity = '0';
    this.button.style.pointerEvents = 'none';
  }

  showButton() {
    if (!this.button || !this.button.style) return;
    this.button.style.transform = 'translateY(0) scale(1)';
    this.button.style.opacity = '1';
    this.button.style.pointerEvents = 'auto';
  }

  setupAccessibility() {
    if (!this.button) return;
    // Ensure proper ARIA attributes
    this.button.setAttribute('aria-label', 'Abrir conversa no WhatsApp');
    this.button.setAttribute('role', 'button');
    
    // Keyboard navigation support
    this.button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (this.button && this.button.click) {
          this.button.click();
        }
      }
    });
  }

  dispatchCustomEvent(eventName, detail) {
    detail = detail || {};
    const event = new CustomEvent(eventName, {
      detail: {
        timestamp: Date.now(),
        url: this.button && this.button.href ? this.button.href : '',
        ...detail
      },
      bubbles: true
    });
    
    document.dispatchEvent(event);
  }

  // Public methods for external control
  show() {
    this.showButton();
  }

  hide() {
    this.hideButton();
  }

  updatePosition(alignment, distances) {
    if (!this.button || !this.button.style) return;
    const { desktopSide, desktopBottom, mobileSide, mobileBottom } = distances;
    
    this.button.setAttribute('data-alignment', alignment);
    this.button.style.setProperty('--desktop-distance-side', `${desktopSide}px`);
    this.button.style.setProperty('--desktop-distance-bottom', `${desktopBottom}px`);
    this.button.style.setProperty('--mobile-distance-side', `${mobileSide}px`);
    this.button.style.setProperty('--mobile-distance-bottom', `${mobileBottom}px`);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof window !== 'undefined') {
      window.whatsappFloat = new WhatsAppFloat();
    }
  });
} else {
  if (typeof window !== 'undefined') {
    window.whatsappFloat = new WhatsAppFloat();
  }
}

// Make available globally for theme customization
if (typeof window !== 'undefined') {
  window.WhatsAppFloat = WhatsAppFloat;
}